<template>
    <el-tabs v-model="activeTab" :class="{ 'public-certificate-tabs': isPublicCertificateRoute }">
        <el-tab-pane v-if="showCertificatePane" label="Certificate" name="certificate">
            <el-button type="primary" v-if="certificateItems.length > 0" :loading="downloading" @click="$emit('export', 'showTotal')"
                size="medium" style="margin-bottom: 10px;">Download the certificate</el-button>
            <p v-if="showNoAwards && !hasAwardItems" style="margin-left: 20px;">You did not receive an award for this competition</p>
            <div id="showTotal">
                <ResponsiveCertificateFrame v-for="(item, index) in certificateItems" :key="index"
                    :designWidth="1123" :designHeight="item.award_set_note && !item.result_cancelled && !item.result_under_review ? 850 : 794">
                    <div class="certContainerWEC" data-certificate-document
                        data-export-width="1123" :data-export-height="item.award_set_note && !item.result_cancelled && !item.result_under_review ? 850 : 794"
                        :style="{ height: (item.award_set_note && !item.result_cancelled && !item.result_under_review ? 850 : 794) + 'px' }"
                        :class="{ has_fraud_result: item.result_fraud, is_ifo_project: isIfoProject }">
                        <img :src="horizontalStudentCertUrl" class="modelCert" crossorigin="anonymous" />
                        <div class="student">{{ studentName }}</div>
                        <div class="school">{{ accountName }}</div>
                        <div v-if="item.result_fraud" class="cert_cancelled_boxed">
                            <div class="cert_cancelled_title">{{ item.award_lable }}</div>
                            <div class="cert_fraud_note">{{ fraudNoticeText }}</div>
                        </div>
                        <template v-else>
                            <div class="award" :class="{ result_cancelled: item.result_cancelled, result_under_review: item.result_under_review, result_invalid: item.result_invalid }">{{ item.award_lable }}</div>
                            <div v-if="item.result_invalid" class="cert_invalid_notice">
                                <div class="cert_invalid_title">SCORE INVALID</div>
                                <div class="cert_invalid_text">{{ scoreInvalidNoticeText }}</div>
                            </div>
                            <p v-else-if="item.result_cancelled" class="tips">This result has been cancelled for the
                                <br>{{ projectInfo.project_ename }}.</p>
                            <p v-else-if="item.result_under_review" class="tips">This result is temporarily unavailable while it is under review.</p>
                            <p v-else class="tips">We appreciate your faith in academic studies and your excellent performance in
                                the <br>{{ projectInfo.project_ename }}.</p>
                        </template>
                        <div class="date">{{ certDate }}</div>
                        <div class="divider"></div>
                        <div class="dateDes">Date</div>
                        <DigitalCertificateQr v-if="supportsDigitalCertificate" :url="certificateUrl"
                            :colorDark="certificateQrColor" class="certificate-qr" />
                        <div class="application-id">Unique Trace Code: {{ applicationId }}</div>
                        <div v-if="item.award_set_note && !item.result_cancelled && !item.result_under_review && !item.result_invalid" class="award-set-note">
                            {{ item.award_set_note }}
                        </div>
                    </div>
                </ResponsiveCertificateFrame>
            </div>
        </el-tab-pane>
        <el-tab-pane v-if="showTranscriptPane" label="Transcript" name="transcript">
            <el-button type="primary" :loading="downloading" @click="$emit('export', 'showTranscript')" size="medium"
                style="margin-bottom: 10px;">Download the transcript</el-button>
            <div id="showTranscript">
                <ResponsiveCertificateFrame :designWidth="794" :designHeight="1123">
                    <div class="transcriptContainerWEC" data-certificate-document
                        data-export-width="794" data-export-height="1123"
                        :class="{ has_invalid_score: showScoreInvalidNotice, has_fraud_result: showFraudCancelled, is_ifo_project: isIfoProject }">
                        <img :src="transcriptBackgroundUrl" class="model_p" crossorigin="anonymous" />
                        <div class="tips1">This certificate is proudly presented to</div>
                        <div class="name">{{ studentName }}</div>
                        <div class="account">{{ accountName }}</div>
                        <div class="tips1 tips1-participation">for participation in the</div>
                        <div class="tips1 tips1-project">{{ projectInfo.project_ename }}</div>
                        <div class="total_score" :class="{ total_score_with_penalties: showScoreBreakdown || showScoreInvalidNotice || showFraudCancelled }">
                            <div v-if="showFraudCancelled" class="transcript_cancelled transcript_cancelled_boxed">
                                <div>Result Cancelled</div>
                                <div class="transcript_fraud_note">{{ fraudNoticeText }}</div>
                            </div>
                            <div v-else-if="isResultCancelled" class="transcript_cancelled">Result Cancelled</div>
                            <div v-else-if="isResultUnderReview" class="transcript_review">
                                <div>Result Under Review</div>
                                <div class="transcript_review_note">Result temporarily unavailable</div>
                            </div>
                            <template v-else>
                                <template v-if="showScoreBreakdown">
                                    <div class="score_breakdown_title">Score Breakdown</div>
                                    <div class="score_breakdown_line"></div>
                                    <div class="score_breakdown_row">
                                        <div class="score_breakdown_label">Raw Score:</div>
                                        <div class="score_breakdown_values">
                                            <div v-for="item in breakdownScoreRows" :key="item.key" class="score_breakdown_item">
                                                - {{ item.name }}: {{ item.rawScore }} / {{ item.fullScore }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="score_breakdown_row">
                                        <div class="score_breakdown_label">Penalties:</div>
                                        <div class="score_breakdown_values">
                                            <div v-if="breakdownPenaltyRows.length <= 0" class="score_breakdown_item">
                                                -
                                            </div>
                                            <div v-for="item in breakdownPenaltyRows" :key="item.key" class="score_breakdown_item">
                                                - {{ item.reason }}: -{{ item.penalty }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="score_breakdown_line"></div>
                                    <div class="score_breakdown_final">
                                        <span>Final Score:</span>
                                        <span>{{ finalScoreTotal }} / {{ fullScoreTotal }}</span>
                                    </div>
                                    <div v-if="scoreWeightNote" class="score_breakdown_note">{{ scoreWeightNote }}</div>
                                </template>
                                <template v-else>
                                    <div v-for="item in scores" :key="item.application_event">
                                        <span style="color: #333;">{{ item.application_event + ' : ' }}</span>
                                        <span style="font-weight: 600;">{{ item.total_score + ' / ' + item.full_score }}</span>
                                    </div>
                                </template>
                                <div v-if="showScoreInvalidNotice" class="transcript_invalid_notice">
                                    <div class="transcript_invalid_title">SCORE INVALID</div>
                                    <div class="transcript_invalid_text">{{ scoreInvalidNoticeText }}</div>
                                </div>
                            </template>
                        </div>
                        <div class="transcript-footer-date">
                            <div class="date">{{ certDate }}</div>
                            <div class="divider"></div>
                            <div class="show_date">Date</div>
                        </div>
                        <DigitalCertificateQr v-if="supportsDigitalCertificate" :url="transcriptUrl"
                            :colorDark="transcriptQrColor" class="transcript-qr" />
                        <div class="application-id2">Unique Trace Code: {{ applicationId }}</div>
                    </div>
                </ResponsiveCertificateFrame>
            </div>
        </el-tab-pane>
    </el-tabs>
</template>

<script>
import DigitalCertificateQr from '../../../components/DigitalCertificateQr.vue'
import ResponsiveCertificateFrame from '../../../components/ResponsiveCertificateFrame.vue'
import {
    buildPublicStudentCertificateUrl,
    getDigitalCertificateConfig,
    isPublicStudentCertificatePath,
    supportsDigitalCertificate as projectSupportsDigitalCertificate,
    supportsScoreBreakdown as projectSupportsScoreBreakdown
} from '../../../config/digitalCertificates'

export default {
    name: 'AwardDefault',
    components: {
        DigitalCertificateQr,
        ResponsiveCertificateFrame
    },
    props: {
        awards: Array,
        scores: Array,
        studentName: String,
        accountName: String,
        projectInfo: Object,
        horizontalStudentCertUrl: String,
        certDate: String,
        applicationId: String,
        showNoAwards: Boolean,
        downloading: Boolean
    },
    data() {
        return {
            activeTab: this.$route.query.tab === 'transcript' ? 'transcript' : 'certificate',
            // TEMP: true = local IFO transcript bg for layout testing; set false to use project config image
            useLocalIfoTranscriptBg: false
        }
    },
    computed: {
        certificateConfig() {
            return getDigitalCertificateConfig(this.projectInfo && this.projectInfo.project_code)
        },
        transcriptBackgroundUrl() {
            if (this.isIfoProject && this.useLocalIfoTranscriptBg) {
                return '/image/ifo/IFO_Student_Transcript.jpg'
            }
            return this.projectInfo && this.projectInfo.vertical_transcript
        },
        isPublicCertificateRoute() {
            return isPublicStudentCertificatePath(this.$route.path)
        },
        showCertificatePane() {
            return !this.isPublicCertificateRoute || this.activeTab == 'certificate'
        },
        showTranscriptPane() {
            return !this.isPublicCertificateRoute || this.activeTab == 'transcript'
        },
        isIfoProject() {
            return !!(this.certificateConfig && this.certificateConfig.ifoLayout)
        },
        isResultCancelled() {
            return (this.scores || []).some(score => score && score.in_vain === true)
        },
        isResultFraud() {
            return (this.scores || []).some(score => score && score.fraud === true)
        },
        showFraudCancelled() {
            return this.isResultFraud
        },
        isResultUnderReview() {
            return !this.isResultCancelled && !this.isResultFraud &&
                (this.scores || []).some(score => score && score.under_review === true)
        },
        isScoreInvalid() {
            return (this.scores || []).some(score => score && score.invalid === true)
        },
        showScoreInvalidNotice() {
            return this.isScoreInvalid && !this.isResultCancelled && !this.isResultUnderReview && !this.isResultFraud
        },
        scoreInvalidNoticeText() {
            return 'Repeated remote-proctoring violations were logged for this candidate. Cumulative misconduct compromises examination integrity and breaches remote‑examination‑environment requirements; the examination result is ruled invalid.'
        },
        fraudNoticeText() {
            return 'The candidate was found to have engaged in severe fraudulent conduct, abusing the applicable terms to obtain unwarranted benefits. This conduct constitutes a fundamental breach of integrity; all examination results are cancelled.'
        },
        hasAwardItems() {
            return Array.isArray(this.awards) && this.awards.length > 0
        },
        certificateItems() {
            const awardItems = this.awards || []
            // Certificates only exist when the student has awards.
            // Result status only changes what an existing certificate shows.
            if (awardItems.length <= 0) {
                return []
            }
            if (this.isResultFraud) {
                return [{
                    award_lable: 'Result Cancelled',
                    result_cancelled: true,
                    result_fraud: true
                }]
            }
            if (this.isResultCancelled) {
                return [{
                    award_lable: 'Result Cancelled',
                    result_cancelled: true
                }]
            }
            if (this.isResultUnderReview) {
                return [{
                    award_lable: 'Result Under Review',
                    result_under_review: true
                }]
            }
            if (this.showScoreInvalidNotice) {
                return awardItems.map((item) => {
                    return Object.assign({}, item, { result_invalid: true })
                })
            }
            return awardItems
        },
        supportsDigitalCertificate() {
            return projectSupportsDigitalCertificate(this.projectInfo && this.projectInfo.project_code)
        },
        certificateQrColor() {
            return (this.certificateConfig && this.certificateConfig.certificateQrColor) || '#E90046'
        },
        transcriptQrColor() {
            return (this.certificateConfig && this.certificateConfig.transcriptQrColor) || '#E90046'
        },
        supportsScoreBreakdown() {
            return projectSupportsScoreBreakdown(this.projectInfo && this.projectInfo.project_code)
        },
        showScoreBreakdown() {
            return this.supportsScoreBreakdown && this.scores && this.scores.length > 0
        },
        scoreRows() {
            return (this.scores || []).map((score, index) => {
                return {
                    key: this.scoreKey(score, index),
                    name: score.application_event || score.mode || 'Score',
                    rawScore: this.formatScoreNumber(this.getRawScore(score)),
                    finalScore: this.formatScoreNumber(this.toNumber(score.total_score)),
                    fullScore: this.formatScoreNumber(this.toNumber(score.full_score))
                }
            })
        },
        penaltyRows() {
            let proctorTotal = 0
            let procedureTotal = 0

            ;(this.scores || []).forEach((score) => {
                let penalties = []
                if (score.penalty_details && score.penalty_details.length > 0) {
                    penalties = score.penalty_details
                } else if (score.penalties && score.penalties.length > 0) {
                    penalties = score.penalties
                }

                penalties.forEach((penalty) => {
                    const amount = Math.abs(this.toNumber(penalty.penalty))
                    if (amount <= 0) {
                        return
                    }
                    if (this.isProctorPenalty(penalty)) {
                        proctorTotal += amount
                        return
                    }
                    // Absence in Mock / Failure in Manual-Submission / other procedure items
                    // all display as one Procedure-Noncompliance Penalty line.
                    procedureTotal += amount
                })
            })

            const rows = []
            if (proctorTotal > 0) {
                rows.push({
                    key: 'proctor-penalty',
                    reason: 'Proctor-Violation Penalty',
                    penalty: this.formatScoreNumber(proctorTotal)
                })
            }
            if (procedureTotal > 0) {
                rows.push({
                    key: 'system-penalty',
                    reason: 'Procedure-Noncompliance Penalty',
                    penalty: this.formatScoreNumber(procedureTotal)
                })
            }
            return rows
        },
        breakdownScoreRows() {
            return this.scoreRows
        },
        breakdownPenaltyRows() {
            return this.penaltyRows
        },
        scoreWeights() {
            return (this.certificateConfig && this.certificateConfig.scoreWeights) || null
        },
        scoreWeightNote() {
            return (this.certificateConfig && this.certificateConfig.scoreWeightNote) || ''
        },
        finalScoreTotal() {
            const moduleTotal = (this.scores || []).reduce((total, score) => {
                return total + this.toNumber(score.total_score) * this.getScoreWeight(score)
            }, 0)
            return this.formatScoreNumber(moduleTotal)
        },
        fullScoreTotal() {
            return this.formatScoreNumber((this.scores || []).reduce((total, score) => {
                return total + this.toNumber(score.full_score) * this.getScoreWeight(score)
            }, 0))
        },
        certificateUrl() {
            return this.publicCertificateUrl('certificate')
        },
        transcriptUrl() {
            return this.publicCertificateUrl('transcript')
        }
    },
    methods: {
        publicCertificateUrl(tab) {
            return buildPublicStudentCertificateUrl(
                this.projectInfo && this.projectInfo.project_code,
                this.applicationId,
                tab
            )
        },
        scoreKey(score, index) {
            return (score.application_event || score.mode || 'score') + '-' + index
        },
        getScoreWeight(score) {
            const weights = this.scoreWeights
            if (!weights) {
                return 1
            }
            const eventName = score && (score.application_event || score.mode)
            if (eventName && Object.prototype.hasOwnProperty.call(weights, eventName)) {
                return this.toNumber(weights[eventName])
            }
            return 0
        },
        getRawScore(score) {
            if (score.original_total_score !== undefined && score.original_total_score !== null && score.original_total_score !== '') {
                return this.toNumber(score.original_total_score)
            }
            return this.toNumber(score.total_score)
        },
        toNumber(value) {
            let numberValue = Number(value)
            return isNaN(numberValue) ? 0 : numberValue
        },
        formatScoreNumber(value) {
            let numberValue = this.toNumber(value)
            return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(2)
        },
        sumFormatted(rows, key) {
            return this.formatScoreNumber((rows || []).reduce((total, row) => {
                return total + this.toNumber(row[key])
            }, 0))
        },
        isProctorPenalty(penalty) {
            if (!penalty || typeof penalty !== 'object') {
                return false
            }
            const autoTrigger = String(penalty.auto_trigger || '').trim().toLowerCase()
            if (autoTrigger === 'remote_exam_proctor') {
                return true
            }
            const text = [
                penalty.type,
                penalty.display_reason,
                penalty.description,
                penalty.system_key
            ].filter(value => value !== undefined && value !== null && String(value).trim() !== '')
                .map(value => String(value).trim().toLowerCase())
                .join(' ')
            return text.indexOf('remote exam') >= 0 ||
                text.indexOf('camera leave') >= 0 ||
                text.indexOf('network disconnect') >= 0 ||
                text === 'talking' ||
                text === 'camera_leave' ||
                text === 'network_disconnect'
        }
    }
}
</script>

<style lang="scss" scoped>
.public-certificate-tabs {
    ::v-deep .el-tabs__header {
        display: none;
    }
}

@media (max-width: 767px) {
    .public-certificate-tabs {
        ::v-deep .el-button {
            width: 100%;
            min-height: 44px;
        }
    }
}

.certContainerWEC {
    position: relative;
    width: 1123px;
    height: 794px;
    font-family: 'Lato', sans-serif;

    .modelCert {
        display: block;
        width: 100%;
        height: 794px;
    }

    .student {
        position: absolute;
        top: 310px;
        width: 297mm;
        font-size: 36px;
        font-weight: 700;
        text-align: center;
    }

    .school {
        width: 297mm;
        position: absolute;
        top: 375px;
        text-align: center;
        font-size: 26px;
        font-weight: 350;
    }

    .award {
        width: 297mm;
        position: absolute;
        top: 435px;
        text-align: center;
        font-size: 32px;
        font-weight: 500;
    }

    .result_cancelled {
        color: #9f2f2f;
    }

    .result_under_review {
        color: #9b6a10;
    }

    .result_invalid {
        color: #9f2f2f;
    }

    .cert_cancelled_boxed {
        position: absolute;
        top: 470px;
        left: 50%;
        transform: translateX(-50%);
        box-sizing: border-box;
        width: 700px;
        max-width: calc(100% - 160px);
        padding: 16px 18px;
        border: 1px solid #9f2f2f;
        border-radius: 8px;
        background: #fff;
        color: #9f2f2f;
        text-align: center;
    }

    .cert_cancelled_title {
        font-size: 32px;
        font-weight: 700;
        letter-spacing: 0.5px;
    }

    .cert_fraud_note {
        margin-top: 12px;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4;
    }

    .cert_invalid_notice {
        position: absolute;
        top: 490px;
        left: 50%;
        transform: translateX(-50%);
        box-sizing: border-box;
        width: 700px;
        max-width: calc(100% - 160px);
        padding: 12px 14px;
        background: #fff;
        border: 1px solid #9f2f2f;
        border-radius: 8px;
        color: #9f2f2f;
        text-align: left;
    }

    .cert_invalid_title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.4px;
        margin-bottom: 6px;
        text-align: center;
    }

    .cert_invalid_text {
        font-size: 13px;
        font-weight: 500;
        line-height: 1.4;
        color: #5c2a2a;
    }

    .tips {
        width: 297mm;
        padding: 0 230px;
        box-sizing: border-box;
        position: absolute;
        top: 495px;
        text-align: center;
        font-size: 18px;
        line-height: 26px;
        font-weight: normal;
    }

    .date {
        text-align: center;
        width: 150px;
        position: absolute;
        top: 610px;
        left: 400px;
        font-size: 18px;
        font-weight: 400;
    }
    .dateDes{
        width: 150px;
        position: absolute;
        top: 650px;
        left: 460px;
        font-size: 18px;
        font-weight: 400;
    }

    .divider {
        position: absolute;
        top: 641px;
        width: 150px;
        color: #000;
        left: 400px;
        font-size: 18px;
        font-weight: 400;
        border-bottom-color: #666666;
        border-bottom-width: 1px;
    }

    .application-id {
        position: absolute;
        top: 700px;
        left: 400px;
        width: 500px;
        text-align: left;
        color: #aaa;
    }

    .certificate-qr {
        position: absolute;
        top: 575px;
        left: 650px;
    }

    // IFO background has "INTERNATIONAL FINANCE OLYMPIAD" on the lower right
    &.is_ifo_project {
        .application-id {
            top: 720px;
        }
    }

    &.has_fraud_result {
        .date {
            top: 670px;
        }

        .divider {
            top: 701px;
        }

        .dateDes {
            top: 710px;
        }

        .certificate-qr {
            top: 635px;
        }

        .application-id {
            top: 755px;
        }
    }

    &.has_fraud_result.is_ifo_project {
        .application-id {
            top: 780px;
        }
    }

    .award-set-note {
        width: 297mm;
        position: absolute;
        top: 750px;
        background: white;
        border: 1px solid #333;
        padding: 15px;
        margin: 0 20px;
        box-sizing: border-box;
        font-size: 16px;
        line-height: 1.4;
        text-align: left;
    }
}

.transcriptContainerWEC {
    position: relative;
    width: 794px;
    height: 1123px;
    overflow: hidden;
    font-family: 'Lato', sans-serif;

    .model_p {
        display: block;
        width: 100%;
        height: 100%;
    }

    .tips1 {
        width: 210mm;
        color: #333;
        position: absolute;
        text-align: center;
        top: 320px;
        font-size: 20px;
        font-weight: normal;
    }

    .tips1-participation {
        top: 510px;
    }

    .tips1-project {
        top: 560px;
        font-size: 24px;
        font-weight: bold;
    }

    .name {
        position: absolute;
        width: 210mm;
        top: 370px;
        font-size: 42px;
        font-weight: 600;
        text-align: center;
    }

    .account {
        text-align: center;
        width: 210mm;
        position: absolute;
        top: 445px;
        font-size: 30px;
        font-weight: 400;
    }

    .total_score {
        border-left-width: 5px;
        border-left-color: #333;
        padding-left: 10px;
        position: absolute;
        top: 605px;
        left: 105px;
        font-size: 22px;
        font-weight: 400;
        height: 120px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .transcript_cancelled {
        color: #9f2f2f;
        font-size: 30px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-align: center;
    }

    .transcript_cancelled_boxed {
        box-sizing: border-box;
        width: 100%;
        max-width: 560px;
        padding: 16px 18px;
        border: 1px solid #9f2f2f;
        border-radius: 8px;
        background: #fff;
    }

    .transcript_fraud_note {
        margin-top: 12px;
        font-size: 13px;
        font-weight: 500;
        line-height: 1.4;
        letter-spacing: 0;
        text-align: center;
        color: #9f2f2f;
    }

    .transcript_review {
        color: #9b6a10;
        font-size: 30px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-align: center;
    }

    .transcript_review_note {
        margin-top: 8px;
        font-size: 18px;
        font-weight: 500;
    }

    .transcript_invalid_notice {
        margin-top: 10px;
        box-sizing: border-box;
        padding: 10px 12px;
        background: #fff;
        border: 1px solid #9f2f2f;
        color: #9f2f2f;
        text-align: left;
    }

    .transcript_invalid_title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.4px;
        margin-bottom: 6px;
    }

    .transcript_invalid_text {
        font-size: 12px;
        font-weight: 500;
        line-height: 1.35;
        color: #5c2a2a;
    }

    &.has_fraud_result {
        .total_score {
            top: 630px;
            height: auto;
            justify-content: flex-start;
        }

        .total_score_with_penalties {
            top: 630px;
        }

        .transcript-footer-date {
            top: 880px;
        }

        .transcript-qr {
            top: 880px;
        }

        .application-id2 {
            top: 1010px;
        }
    }

    &.has_invalid_score {
        .total_score {
            top: 590px;
            height: auto;
            justify-content: flex-start;
        }

        .total_score_with_penalties {
            top: 590px;
        }

        .transcript-footer-date {
            top: 880px;
        }

        .transcript-qr {
            top: 880px;
        }

        .application-id2 {
            top: 1010px;
        }
    }

    .total_score_with_penalties {
        top: 620px;
        left: 116px;
        width: 560px;
        height: auto;
        padding-left: 0;
        border-left-width: 0;
        font-size: 15px;
        line-height: 1.18;
        display: block;

        .score_breakdown_title {
            margin-bottom: 3px;
            text-align: center;
            font-size: 16px;
            font-weight: 700;
        }

        .score_breakdown_line {
            width: 100%;
            border-top: 1px solid #111;
        }

        .score_breakdown_row {
            display: grid;
            grid-template-columns: 112px 1fr;
            column-gap: 12px;
            margin: 4px 10px;
        }

        .score_breakdown_label {
            font-weight: 700;
        }

        .score_breakdown_values {
            text-align: right;
        }

        .score_breakdown_item {
            margin-bottom: 1px;
            word-break: normal;
            overflow-wrap: anywhere;
        }

        .score_breakdown_final {
            display: grid;
            grid-template-columns: 1fr 128px;
            column-gap: 14px;
            margin-top: 4px;
            padding-right: 10px;
            text-align: right;
            font-weight: 700;
        }

        .score_breakdown_note {
            margin: 4px 10px 0;
            color: #888;
            font-size: 11px;
            font-weight: 400;
            line-height: 1.35;
            text-align: right;
        }
    }

    // Date block and QR: same top + same height => vertical centers align
    // left/right margin both 200px on 794px page => horizontal symmetry
    // top clears Final Score + ~1 extra line for up to 2 penalty rows
    .transcript-footer-date {
        position: absolute;
        top: 820px;
        left: 200px;
        width: 150px;
        height: 116px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 18px;
        font-weight: 400;
    }

    .transcript-footer-date .date {
        position: static;
        width: auto;
        top: auto;
        left: auto;
        font-size: 18px;
        font-weight: 400;
        text-align: center;
    }

    .transcript-footer-date .divider {
        position: static;
        top: auto;
        left: auto;
        width: 150px;
        margin: 10px 0 6px;
        border-bottom-color: #666666;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        color: #000;
        font-size: 18px;
        font-weight: 400;
    }

    .transcript-footer-date .show_date {
        position: static;
        top: auto;
        left: auto;
        width: auto;
        font-size: 18px;
        font-weight: 400;
        text-align: center;
    }

    .transcript-qr {
        position: absolute;
        top: 820px;
        left: 454px; // 794 - 200 - 140, equal side margins with date
    }

    .application-id2 {
        position: absolute;
        top: 950px;
        left: 0;
        width: 794px;
        text-align: center;
        color: #666;
    }

    // IFO: only nudge Unique Trace Code down to clear the right-side logo
    &.is_ifo_project {
        .application-id2 {
            top: 985px;
        }
    }

    // Invalid/fraud notice must not push IFO footer into the logo / Unique Trace Code
    &.has_invalid_score.is_ifo_project,
    &.has_fraud_result.is_ifo_project {
        .transcript-footer-date {
            top: 820px;
        }

        .transcript-qr {
            top: 820px;
        }

        .application-id2 {
            top: 985px;
        }
    }
}
</style>
